// The machine import's preparation, as the wizard does it (pages/Import.tsx, M2), in one pure function (M22, docs/STACK.md 9.6 and 14.3): the page calls
// it, and so does the operator's Node path for production (ops/dev/import-machines.mjs --target / --direct), so tags, kinds and defaults are decided
// exactly as the page decides them. No browser, no React, no IO: it is loaded under `node --experimental-strip-types` too (no parameter properties,
// enums or namespaces here or in what it imports; type-only imports are `import type`).
import type { AssetImportStageV1, AssetKind } from '@gk/contracts'
import { generateTags, type Mapping } from './mapping.ts'
import type { ParsedTable } from './readTable.ts'
import { buildStagePayloads } from './stage.ts'

export interface PrepareImportInput {
  /** the file as read (readTableFile): headers, rows of cells, csv or xlsx */
  table: Pick<ParsedTable, 'headers' | 'rows' | 'source'>
  /** the columns the person confirmed */
  mapping: Mapping
  /** the file's kind words and the kind chosen for each; a word left without one ('' or absent) is not sent (the server flags it) */
  kindMap: Record<string, AssetKind | '' | undefined>
  /** the kind for rows whose own kind column says nothing ('' or null: none chosen) */
  defaultKind: AssetKind | '' | null
  /** the prefix of generated tags (used only when no tag column is mapped) */
  prefix: string
  /** the club's tags already in use (the device's mirror plus overlay; the operator's path reads the club's fleet.asset) */
  existingTags: Iterable<string>
  orgId: string
  fileName: string
  /** the file's SHA-256 (hex): the batch id is v5(org, this) */
  sha256: string
}
export interface PreparedImport {
  /** the asset.import_stage@1 payloads, in order (chunks of stage.ts CHUNK rows) */
  payloads: AssetImportStageV1[]
  /** the tags proposed for the rows, one per row, when the file has no tag column; undefined otherwise */
  generatedTags: string[] | undefined
  batchId: string
}

/** Tags only when no tag column is mapped; the kind map keeps only the words given a kind; defaults hold `{kind}` only when a default kind is chosen. */
export function prepareImport(i: PrepareImportInput): PreparedImport {
  const generatedTags = i.mapping.readable_tag ? undefined : generateTags(i.table.rows.length, i.prefix, i.existingTags)
  const kindMap = Object.fromEntries(Object.entries(i.kindMap).filter((e): e is [string, AssetKind] => !!e[1])) as Record<string, AssetKind>
  const payloads = buildStagePayloads({
    orgId: i.orgId, fileName: i.fileName, sha256: i.sha256, source: i.table.source,
    headers: i.table.headers, rows: i.table.rows, mapping: i.mapping,
    kindMap,
    defaults: i.defaultKind ? { kind: i.defaultKind } : {},
    ...(generatedTags ? { generatedTags } : {}),
  })
  return { payloads, generatedTags, batchId: payloads[0]!.batch_id }
}
